import React, { useCallback, useEffect, useRef, useState } from 'react';
import { pulseSurveyAPI, systemAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { ClipboardList, ListChecks } from 'lucide-react';
import { ClickableKpiCard, KpiDetailPanel } from './common/ClickableKpiCard';

const EmployeePulseSurveyInbox = () => {
  const [loading, setLoading] = useState(true);
  const [inbox, setInbox] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [surveyDetail, setSurveyDetail] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastDraftSavedAt, setLastDraftSavedAt] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [featureBanner, setFeatureBanner] = useState('');
  const [cardDetail, setCardDetail] = useState(null);
  const draftHydrationRef = useRef(false);
  const typingDebounceRef = useRef(null);
  const savingDraftRef = useRef(false);

  const loadInbox = useCallback(async () => {
    try {
      const res = await pulseSurveyAPI.getMyInbox();
      setInbox(res.data || []);
      if (!selectedSurveyId && res.data?.length) {
        setSelectedSurveyId(res.data[0].survey_id);
      }
    } catch (error) {
      // Backward compatibility if inbox endpoint or new tables are not fully migrated yet.
      const fallback = await pulseSurveyAPI.listSurveys().catch(() => ({ data: [] }));
      const fallbackItems = (fallback.data || []).map((item) => ({
        survey_id: item.id,
        title: item.title,
        description: item.description,
        status: item.status,
        is_anonymous: item.is_anonymous,
        starts_at: item.starts_at,
        ends_at: item.ends_at,
        invitation_status: 'invited',
        responded_at: null,
        can_respond: item.status === 'live',
        has_draft: false,
        due_in_days: null,
        is_overdue: false,
      }));
      setInbox(fallbackItems);
      if (!selectedSurveyId && fallbackItems.length) {
        setSelectedSurveyId(fallbackItems[0].survey_id);
      }
      // Keep one clear message instead of hard failure.
      if (!fallbackItems.length) {
        setFeatureBanner('Survey inbox service unavailable. Please run backend migration and restart service.');
      }
    }
  }, [selectedSurveyId]);

  const applyDraftAnswers = useCallback((draftAnswers = [], questions = []) => {
    draftHydrationRef.current = true;
    const questionTypeById = new Map((questions || []).map((q) => [q.id, q.question_type]));
    const next = {};
    for (const ans of draftAnswers || []) {
      const qId = Number(ans.question_id);
      const qType = questionTypeById.get(qId);
      if (!qId || !qType) continue;
      if (qType === 'text') {
        next[qId] = { answer_text: ans.answer_text || '' };
      } else if (qType === 'rating' || qType === 'nps') {
        next[qId] = { answer_numeric: ans.answer_numeric ?? '' };
      } else if (qType === 'single_choice') {
        const option = Array.isArray(ans.answer_options) ? ans.answer_options[0] : '';
        next[qId] = { answer_option: option || '' };
      } else if (qType === 'multi_choice') {
        next[qId] = { answer_options: Array.isArray(ans.answer_options) ? ans.answer_options : [] };
      }
    }
    setAnswers(next);
    setHasUnsavedChanges(false);
    setTimeout(() => {
      draftHydrationRef.current = false;
    }, 0);
  }, []);

  const loadMyDraft = useCallback(async (surveyId, questions) => {
    if (!surveyId) return;
    const res = await pulseSurveyAPI.getMyDraft(surveyId);
    applyDraftAnswers(res.data?.answers || [], questions || []);
    setLastDraftSavedAt(res.data?.updated_at || null);
  }, [applyDraftAnswers]);

  const loadSurveyDetail = useCallback(async (surveyId) => {
    if (!surveyId) return;
    const res = await pulseSurveyAPI.getSurveyDetail(surveyId);
    const detail = res.data || null;
    setSurveyDetail(detail);
    await loadMyDraft(surveyId, detail?.questions || []);
  }, [loadMyDraft]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const health = await systemAPI.getFeatureHealth().catch(() => null);
        if (health?.data?.features?.pulse_surveys?.migration_required) {
          setFeatureBanner('Survey inbox setup pending. Please run backend migration and restart service.');
        } else {
          setFeatureBanner('');
        }
        await loadInbox();
      } finally {
        setLoading(false);
      }
    })();
  }, [loadInbox]);

  useEffect(() => {
    (async () => {
      try {
        await loadSurveyDetail(selectedSurveyId);
      } catch (error) {
        toast.error('Failed to load survey details');
      }
    })();
  }, [selectedSurveyId, loadSurveyDetail]);

  useEffect(() => {
    if (!selectedSurveyId) return;
    if (draftHydrationRef.current) return;
    if (typingDebounceRef.current) {
      clearTimeout(typingDebounceRef.current);
    }
    typingDebounceRef.current = setTimeout(() => {
      setHasUnsavedChanges(true);
    }, 1200);
    return () => {
      if (typingDebounceRef.current) {
        clearTimeout(typingDebounceRef.current);
      }
    };
  }, [answers, selectedSurveyId]);

  const setAnswerValue = (questionId, payload) => {
    setAnswers((prev) => ({ ...prev, [questionId]: payload }));
  };

  const buildAnswerPayload = useCallback((question) => {
    const raw = answers[question.id] || {};
    if (question.question_type === 'text') {
      return {
        question_id: question.id,
        answer_text: raw.answer_text || '',
      };
    }
    if (question.question_type === 'rating' || question.question_type === 'nps') {
      return {
        question_id: question.id,
        answer_numeric: Number(raw.answer_numeric || 0),
      };
    }
    if (question.question_type === 'single_choice') {
      return {
        question_id: question.id,
        answer_options: raw.answer_option ? [raw.answer_option] : [],
      };
    }
    if (question.question_type === 'multi_choice') {
      return {
        question_id: question.id,
        answer_options: raw.answer_options || [],
      };
    }
    return { question_id: question.id };
  }, [answers]);

  const validateRequiredAnswers = () => {
    const questions = surveyDetail?.questions || [];
    for (const q of questions) {
      if (!q.is_required) continue;
      const raw = answers[q.id] || {};
      if (q.question_type === 'text' && !(raw.answer_text || '').trim()) return false;
      if ((q.question_type === 'rating' || q.question_type === 'nps') && (raw.answer_numeric === undefined || raw.answer_numeric === '')) return false;
      if (q.question_type === 'single_choice' && !raw.answer_option) return false;
      if (q.question_type === 'multi_choice' && (!Array.isArray(raw.answer_options) || raw.answer_options.length === 0)) return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!selectedSurveyId || !surveyDetail?.questions?.length) return;
    if (!validateRequiredAnswers()) {
      toast.error('Please complete all required questions');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        answers: surveyDetail.questions.map((q) => buildAnswerPayload(q)),
      };
      await pulseSurveyAPI.submitResponse(selectedSurveyId, payload);
      toast.success('Survey submitted. Thank you!');
      await loadInbox();
      await loadSurveyDetail(selectedSurveyId);
      setAnswers({});
      setLastDraftSavedAt(null);
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error('Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  const saveDraftInternal = useCallback(async (showToast = true) => {
    if (!selectedSurveyId || !surveyDetail?.questions?.length) return;
    if (savingDraftRef.current) return;
    try {
      savingDraftRef.current = true;
      setSavingDraft(true);
      const payload = {
        answers: surveyDetail.questions.map((q) => buildAnswerPayload(q)),
      };
      await pulseSurveyAPI.saveMyDraft(selectedSurveyId, payload);
      const nowIso = new Date().toISOString();
      setLastDraftSavedAt(nowIso);
      setHasUnsavedChanges(false);
      await loadInbox();
      if (showToast) {
        toast.success('Draft saved');
      }
    } catch (error) {
      if (showToast) {
        toast.error('Failed to save draft');
      }
    } finally {
      setSavingDraft(false);
      savingDraftRef.current = false;
    }
  }, [buildAnswerPayload, loadInbox, selectedSurveyId, surveyDetail]);

  const handleSaveDraft = async () => {
    await saveDraftInternal(true);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const currentSurvey = inbox.find((item) => item.survey_id === selectedSurveyId);
      const canAutoSave =
        !!selectedSurveyId &&
        !!surveyDetail?.questions?.length &&
        !!currentSurvey?.can_respond &&
        hasUnsavedChanges &&
        !submitting &&
        !savingDraftRef.current;
      if (canAutoSave) {
        saveDraftInternal(false);
      }
    }, 30000);
    return () => clearInterval(timer);
  }, [inbox, selectedSurveyId, surveyDetail, hasUnsavedChanges, submitting, saveDraftInternal]);

  const getDueBadgeText = (item) => {
    if (item?.due_in_days === null || item?.due_in_days === undefined) return null;
    if (item.due_in_days < 0) return `Overdue by ${Math.abs(item.due_in_days)}d`;
    if (item.due_in_days === 0) return 'Due today';
    return `Due in ${item.due_in_days}d`;
  };

  const selectedInboxItem = inbox.find((item) => item.survey_id === selectedSurveyId);
  const pendingItems = inbox.filter((item) => item.invitation_status !== 'responded');
  const urgentPending = pendingItems.filter((item) => item.is_overdue || (item.due_in_days !== null && item.due_in_days <= 2));
  const completedItems = inbox.filter((item) => item.invitation_status === 'responded');

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading survey inbox...</div>;
  }

  return (
    <div className="space-y-6">
      {featureBanner && (
        <Card className="border-amber-300/50 bg-amber-50">
          <CardContent className="pt-4 text-hr-sm text-amber-900">{featureBanner}</CardContent>
        </Card>
      )}
      <div>
        <h1 className="text-hr-h1 font-bold">Survey Inbox</h1>
        <p className="text-muted-foreground text-hr-sm">Respond to pulse surveys assigned to you.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <ClickableKpiCard
          title="Assigned Surveys"
          value={inbox.length}
          hint="Click to open detailed page."
          icon={ClipboardList}
          cardClassName="border-primary/20 bg-primary/5"
          onClick={() =>
            setCardDetail({
              title: 'Assigned Surveys',
              value: inbox.length,
              description: inbox.length
                ? `${inbox.length} surveys are assigned to you. Open module to focus on the next available survey.`
                : 'No surveys are assigned right now. You will see assigned surveys here once published.',
            })
          }
        />
        <ClickableKpiCard
          title="Pending Responses"
          value={pendingItems.length}
          hint={`${completedItems.length} completed`}
          icon={ListChecks}
          cardClassName="border-[hsl(var(--chart-2))]/20 bg-[hsl(var(--chart-2))]/10"
          onClick={() =>
            setCardDetail({
              title: 'Pending Responses',
              value: pendingItems.length,
              description: pendingItems.length
                ? `${pendingItems.length} survey responses are pending. Prioritize near-due and overdue items first.`
                : 'No pending surveys. All assigned surveys are completed.',
            })
          }
        />
      </div>

      <KpiDetailPanel
        detail={cardDetail}
        onClose={() => setCardDetail(null)}
        onOpenModule={() => {
          if (pendingItems.length > 0) {
            setSelectedSurveyId(pendingItems[0].survey_id);
          } else if (inbox.length > 0) {
            setSelectedSurveyId(inbox[0].survey_id);
          }
          setCardDetail(null);
        }}
        className="border-primary/20 bg-primary/5"
      />

      {pendingItems.length > 0 && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-4 flex flex-wrap items-center gap-2">
            <Badge variant="outline">{pendingItems.length} pending</Badge>
            {urgentPending.length > 0 && (
              <Badge variant="destructive">{urgentPending.length} need attention soon</Badge>
            )}
            <p className="text-sm text-muted-foreground">
              Reminder: complete pending surveys before due date. Near-due and overdue surveys are highlighted.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-1">
          <CardHeader>
            <CardTitle>Assigned Surveys</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[620px] overflow-y-auto">
            {inbox.map((item) => (
              <button
                key={item.survey_id}
                type="button"
                onClick={() => setSelectedSurveyId(item.survey_id)}
                className={`w-full text-left p-3 border rounded-lg hover:bg-muted/40 ${selectedSurveyId === item.survey_id ? 'border-primary bg-primary/5' : ''}`}
              >
                <p className="font-medium">{item.title}</p>
                <div className="mt-2 flex gap-2">
                  <Badge variant="outline">{item.status}</Badge>
                  <Badge variant={item.invitation_status === 'responded' ? 'success' : 'warning'}>
                    {item.invitation_status}
                  </Badge>
                  {item.has_draft && item.invitation_status !== 'responded' && (
                    <Badge variant="outline">Draft saved</Badge>
                  )}
                  {getDueBadgeText(item) && (
                    <Badge variant={item.is_overdue ? 'destructive' : 'outline'}>
                      {getDueBadgeText(item)}
                    </Badge>
                  )}
                </div>
              </button>
            ))}
            {inbox.length === 0 && <p className="text-sm text-muted-foreground">No assigned surveys.</p>}
          </CardContent>
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>{surveyDetail?.survey?.title || 'Select a survey'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {surveyDetail?.survey?.description && (
              <p className="text-sm text-muted-foreground">{surveyDetail.survey.description}</p>
            )}

            {(surveyDetail?.questions || []).map((question) => (
              <div key={question.id} className="rounded-md border p-3 space-y-2">
                <Label className="font-medium">
                  {question.sort_order}. {question.question_text}
                  {question.is_required && <span className="text-destructive ml-1">*</span>}
                </Label>

                {(question.question_type === 'rating' || question.question_type === 'nps') && (
                  <Input
                    type="number"
                    min={question.question_type === 'nps' ? 0 : 1}
                    max={question.question_type === 'nps' ? 10 : 5}
                    value={answers[question.id]?.answer_numeric ?? ''}
                    onChange={(e) =>
                      setAnswerValue(question.id, { ...answers[question.id], answer_numeric: e.target.value })
                    }
                  />
                )}

                {question.question_type === 'text' && (
                  <Textarea
                    rows={3}
                    value={answers[question.id]?.answer_text || ''}
                    onChange={(e) =>
                      setAnswerValue(question.id, { ...answers[question.id], answer_text: e.target.value })
                    }
                  />
                )}

                {question.question_type === 'single_choice' && (
                  <Select
                    value={answers[question.id]?.answer_option || ''}
                    onValueChange={(v) => setAnswerValue(question.id, { ...answers[question.id], answer_option: v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select one" /></SelectTrigger>
                    <SelectContent>
                      {(question.options_json || []).map((opt) => (
                        <SelectItem key={String(opt)} value={String(opt)}>{String(opt)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {question.question_type === 'multi_choice' && (
                  <div className="space-y-1">
                    {(question.options_json || []).map((opt) => {
                      const selected = (answers[question.id]?.answer_options || []).includes(String(opt));
                      return (
                        <button
                          key={String(opt)}
                          type="button"
                          onClick={() => {
                            const current = answers[question.id]?.answer_options || [];
                            const updated = selected
                              ? current.filter((x) => x !== String(opt))
                              : [...current, String(opt)];
                            setAnswerValue(question.id, { ...answers[question.id], answer_options: updated });
                          }}
                          className={`px-2 py-1 border rounded mr-2 mb-2 text-sm ${selected ? 'bg-primary/10 border-primary' : ''}`}
                        >
                          {String(opt)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            {(surveyDetail?.questions || []).length === 0 && (
              <p className="text-sm text-muted-foreground">No questions available in this survey.</p>
            )}

            {selectedInboxItem?.can_respond ? (
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {lastDraftSavedAt ? `Last draft saved: ${new Date(lastDraftSavedAt).toLocaleString()}` : 'No draft saved yet'}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleSaveDraft} disabled={submitting || savingDraft}>
                    {savingDraft ? 'Saving...' : 'Save Draft'}
                  </Button>
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Response'}
                </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {selectedInboxItem?.invitation_status === 'responded'
                  ? 'You have already submitted this survey.'
                  : 'This survey is currently not open for response.'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmployeePulseSurveyInbox;
